import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ImagePlus, Loader2, Save, Send, Trash2, X } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import BlogEditor, { uploadPhoto } from "@/components/blog/BlogEditor";
import { StatusPill, useBlogSettings } from "@/components/blog/BlogBits";

const EMPTY_DOC = { type: "doc", content: [{ type: "paragraph" }] };

function CoverPicker({ imageId, onChange }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try { onChange((await uploadPhoto(file)).id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div data-testid="cover-picker">
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Cover photo (optional)</p>
      {imageId ? (
        <div className="relative mt-2 overflow-hidden rounded-2xl">
          <img src={`${API}/blog/images/${imageId}`} alt="" className="max-h-72 w-full object-cover" data-testid="cover-preview" />
          <button type="button" onClick={() => onChange(null)} className="absolute right-3 top-3 rounded-full bg-background/90 p-1.5 text-foreground shadow hover:text-destructive" aria-label="Remove cover" data-testid="cover-remove"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <button type="button" disabled={busy} onClick={() => ref.current?.click()} className="mt-2 flex h-32 w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-border text-sm text-muted-foreground hover:bg-muted disabled:opacity-60" data-testid="cover-upload">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} {busy ? "Uploading…" : "Add a cover photo (JPEG, PNG or WebP, under 8 MB)"}
        </button>
      )}
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="cover-file-input" />
    </div>
  );
}

export default function BlogWrite() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: settings } = useBlogSettings();
  const { data: existing, isLoading } = useQuery({ queryKey: ["blog-post-edit", id], queryFn: async () => (await api.get(`/blog/post/${id}`)).data, enabled: !!id });
  const [form, setForm] = useState({ title: "", category: "", excerpt: "", cover_image_id: null, doc: EMPTY_DOC });
  const [busy, setBusy] = useState(null);
  const [ready, setReady] = useState(!id);
  const categories = useMemo(() => (settings?.categories || []).filter(Boolean), [settings]);

  useEffect(() => {
    if (existing) { setForm({ title: existing.title, category: existing.category, excerpt: existing.excerpt || "", cover_image_id: existing.cover_image_id, doc: existing.doc }); setReady(true); }
  }, [existing]);
  useEffect(() => { if (!id && settings?.categories?.length && !form.category) setForm((f) => ({ ...f, category: settings.categories[0] })); }, [settings, id, form.category]);

  if (!authLoading && !user) {
    return <div className="mx-auto max-w-xl px-4 py-24 text-center" data-testid="blog-write-signin"><p className="font-serif text-3xl text-foreground">Sign in to write for the blog</p><p className="mt-3 text-muted-foreground">Share how asking questions has shaped your walk with God. Use the Sign in button at the top of the page.</p></div>;
  }
  if (id && (isLoading || !ready)) return <div className="mx-auto max-w-4xl px-4 py-16"><div className="h-96 animate-pulse rounded-2xl bg-muted" /></div>;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const invalidate = () => ["blog-posts", "blog-mine", "blog-post", "blog-post-edit", "admin-blog"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const save = async (submit) => {
    if (form.title.trim().length < 3) { toast.error("Give your post a title."); return; }
    setBusy(submit ? "submit" : "save");
    try {
      const body = { title: form.title.trim(), category: form.category, excerpt: form.excerpt.trim(), cover_image_id: form.cover_image_id, doc: form.doc };
      const { data: post } = id ? await api.put(`/blog/posts/${id}`, body) : await api.post("/blog/posts", body);
      if (submit) {
        const { data } = await api.post(`/blog/posts/${post.id}/submit`);
        toast.success(data.status === "published" ? "Published!" : "Submitted — a moderator will review it shortly.");
      } else {
        toast.success("Draft saved.");
      }
      invalidate();
      navigate(submit ? "/blog/mine" : `/blog/edit/${post.id}`, { replace: true });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    try { await api.delete(`/blog/posts/${id}`); toast.success("Post deleted."); invalidate(); navigate("/blog/mine", { replace: true }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const isAdmin = user?.role === "admin";
  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6" data-testid="blog-write">
      <Seo title={id ? "Edit post — QuestionGod Blog" : "Write a post — QuestionGod Blog"} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/blog/mine" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="write-back"><ArrowLeft className="h-4 w-4" /> My posts</Link>
        {existing && <div className="flex items-center gap-2"><StatusPill status={existing.status} />{existing.status === "published" && !isAdmin && <span className="text-xs text-muted-foreground">Saving changes sends this post back for review.</span>}</div>}
      </div>
      <h1 className="mt-4 font-serif text-4xl font-bold tracking-tight text-foreground sm:text-5xl">{id ? "Edit your post" : "Write a post"}</h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Tell us how a question drew you closer to God, what studying questions has taught you, or how asking changed something. {isAdmin ? "As an admin, your posts publish immediately." : "Posts are reviewed by a moderator before they go live."}</p>

      <div className="mt-8 space-y-6">
        <Input value={form.title} onChange={set("title")} maxLength={140} placeholder="Title" className="h-14 rounded-2xl font-serif text-2xl" data-testid="post-title" />
        <div className="grid gap-6 sm:grid-cols-[220px_1fr]">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Category</p>
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger className="mt-2 rounded-full" data-testid="post-category"><SelectValue placeholder="Choose a category" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Short summary (optional, shown on cards)</p>
            <Textarea value={form.excerpt} onChange={set("excerpt")} maxLength={300} rows={2} className="mt-2 rounded-2xl" placeholder="One or two sentences…" data-testid="post-excerpt" />
          </div>
        </div>
        <CoverPicker imageId={form.cover_image_id} onChange={(v) => setForm((f) => ({ ...f, cover_image_id: v }))} />
        <BlogEditor value={form.doc} onChange={(doc) => setForm((f) => ({ ...f, doc }))} />
      </div>

      <div className="sticky bottom-4 mt-8 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-background/95 p-4 shadow-lg backdrop-blur">
        {id ? <Button variant="ghost" onClick={remove} className="rounded-full text-destructive hover:text-destructive" data-testid="post-delete"><Trash2 className="mr-2 h-4 w-4" /> Delete</Button> : <span />}
        <div className="flex gap-2">
          <Button variant="outline" disabled={!!busy} onClick={() => save(false)} className="rounded-full" data-testid="post-save-draft">{busy === "save" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save draft</Button>
          <Button disabled={!!busy} onClick={() => save(true)} className="rounded-full bg-primary text-primary-foreground" data-testid="post-submit">{busy === "submit" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} {isAdmin ? "Publish" : "Submit for review"}</Button>
        </div>
      </div>
    </div>
  );
}
